// frontend/src/components/homepage/HowSteps.js
//
// The three steps of "how it works" - the copy, and the rail that lights them
// one by one. Shared by the pinned desktop section and the mobile stack, so the
// two can never disagree about what the product does.
//
// COPY IS VERBATIM from the design prototype and owner-approved. It is
// in-app/marketing surface copy, so it says "הזדמנות"; the SEO-facing prose
// elsewhere carries "לידים". The one English H2 is part of the approved design.

import React from "react";

/** The section's copy, one object per language. */
export const HOW_COPY = {
  he: {
    eyebrow: "איך זה עובד",
    h2: "Where Opportunities Find You",
    sub: "אנחנו מזהים את ההזדמנויות ברגע שהן נוצרות ומביאים אותן עד אליך",
    stageWord: "שלב",
    scrollMore: "גללו להמשך",
    pushApp: "Leadify AI",
    pushNow: "עכשיו",
    // Owner-requested 2026-09-04: the push title is "הזדמנות חדשה", without "חמה".
    pushPrefix: "הזדמנות חדשה: ",
    pushDefault: "הזדמנות חדשה: מחפש עורך דין תותח שמתמחה בדיני עבודה מאזור באר שבע...",
    labScan: "סורק",
    labAnalyze: "מנתח",
    steps: [
      {
        tag: "SCAN · 24/7",
        title: "ניטור רציף",
        body: "המערכת שלנו מנטרת קבוצות פייסבוק סביב השעון ומזהה כל פוסט שעשוי להסתיר הזדמנות עסקית.",
      },
      {
        tag: "ANALYZE · SCORE · MATCH",
        title: "ניתוח, דירוג והתאמה",
        body: "לכל פוסט שמזוהה, ה-AI שלנו מנתח, מדרג ומתאים אותו למקצוע ולאזור הגיאוגרפי שלך - רק מה שרלוונטי באמת מגיע אליך.",
      },
      {
        tag: "PUSH · EMAIL · READY",
        title: "התראה מיידית ופנייה מוכנה",
        body: "תקבל התראת Push מיידית לנייד - ללא הגבלה יומית - עם פרטי הליד ופנייה מנוסחת על ידי AI, מוכנה לשליחה. במקביל נשלחת גם התראה למייל.",
      },
    ],
  },
  // The English page has no approved copy for this section yet; these are
  // straight renderings of the Hebrew, pending owner approval like the rest of
  // the English surface.
  en: {
    eyebrow: "How it works",
    h2: "Where Opportunities Find You",
    sub: "We spot opportunities the moment they appear and bring them straight to you",
    stageWord: "Stage",
    scrollMore: "Scroll on",
    pushApp: "Leadify AI",
    pushNow: "now",
    pushPrefix: "New opportunity: ",
    pushDefault: "New opportunity: looking for a top employment lawyer in the Beer Sheva area...",
    labScan: "SCAN",
    labAnalyze: "ANALYZE",
    steps: [
      {
        tag: "SCAN · 24/7",
        title: "Continuous monitoring",
        body: "Our system monitors Facebook groups around the clock and spots every post that may hide a business opportunity.",
      },
      {
        tag: "ANALYZE · SCORE · MATCH",
        title: "Analysis, scoring and matching",
        body: "For every post it finds, our AI analyzes, scores and matches it to your profession and area - only what is genuinely relevant reaches you.",
      },
      {
        tag: "PUSH · EMAIL · READY",
        title: "Instant alert and a ready-made reply",
        body: "You get an instant push alert on your phone - with no daily limit - with the lead's details and an AI-written reply, ready to send. An email alert goes out at the same time.",
      },
    ],
  },
};

const NUMBERS = ["01", "02", "03"];

/**
 * @param {object} props
 * @param {object} props.t the language's HOW_COPY entry.
 * @param {number} props.stage 1 | 2 | 3 - steps below it are on, the one at it is "now".
 * @param {React.Ref} [props.railRef] the growing rail; its `transform: scaleY()`
 *   is written imperatively by the scroll driver (never its height).
 * @param {boolean} [props.railFull] the still pictures: the rail fully grown,
 *   nothing driving it.
 */
export default function HowSteps({ t, stage, railRef, railFull = false }) {
  return (
    <div className="hl-how-steps">
      <i
        className="hl-how-rail"
        ref={railRef}
        style={railFull ? { transform: "scaleY(1)" } : undefined}
        aria-hidden="true"
      />
      {t.steps.map((step, index) => {
        const on = index < stage;
        const now = index === stage - 1;
        const last = index === t.steps.length - 1;
        const className = [
          "hl-how-step",
          on ? "hl-on" : "",
          now ? "hl-now" : "",
          last ? "hl-last" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <div className={className} data-step={index + 1} key={step.tag}>
            <div className="hl-how-n">{NUMBERS[index]}</div>
            <div>
              <span className="hl-how-tag">{step.tag}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
